import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'

import ConsultationsTable from '@/pages/consultations/components/ConsultationsTable'

import NoResults from '@/assets/svg/no-results.svg?react'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  getConsultations,
  selectConsultations,
  selectConsultationSearchParams,
  selectConsultationSearchResult,
  updateConsultation,
} from '@/redux/reducers/consultation.reducer'
import { ConsultationStatus } from '@/core/models/consultation.model'
import { useMemo } from 'react'
import { isStaff } from '@/utils/type-guards'

export const limitOptions = [5, 10, 15]

export function ConsultationsTableContainer() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const consultations = useAppSelector(selectConsultations)
  const consultationSearchParams = useAppSelector(selectConsultationSearchParams)
  const consultationSearchResult = useAppSelector(selectConsultationSearchResult)

  const consultationsData = useMemo(
    () =>
      consultations
        .map((consultation) => ({
          id: consultation._id,
          doctor: consultation.medecin ? _.pick(consultation.medecin.userInfo, ['gender', 'name', 'surname']) : undefined,
          patient: _.pick(consultation.patient.userInfo, ['gender', 'name', 'surname']),
          pds: consultation.pds ? _.pick(consultation.pds.userInfo, ['gender', 'name', 'surname']) : undefined,
          showButtons: {
            accept:
              consultation.status === 'scheduled' &&
              authUser?._id !== (typeof consultation.createdBy === 'string' ? consultation.createdBy : consultation.createdBy._id) &&
              !consultation.waitingRoom,
            live: _.some(['scheduled', 'active', 'accepted'], (status) => status === consultation.status),
            refuse:
              consultation.status === 'scheduled' &&
              authUser?._id !== (typeof consultation.createdBy === 'string' ? consultation.createdBy : consultation.createdBy._id) &&
              !consultation.waitingRoom,
          },
          startDate: consultation.startDate,
          status: consultation.waitingRoom && consultation.status === ConsultationStatus.Scheduled ? 'waiting' : consultation.status,
          type: consultation.type as string,
          groupId: consultation.groupId,
        }))
        .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
    [authUser, consultations]
  )

  const handleChangePage = (page: number) => {
    const limit = _.get(consultationSearchParams, 'options.limit', limitOptions[0])
    const query = _.get(consultationSearchParams, 'query', {})
    dispatch(getConsultations(query, { limit, skip: (page - 1) * limit, order: 'DESC', orderBy: 'startDate' }))
  }

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    const query = _.get(consultationSearchParams, 'query', {})
    dispatch(getConsultations(query, { limit: rowsPerPage, order: 'DESC', orderBy: 'startDate' }))
  }

  const handleAcceptConsultation = (consultationId: string) => {
    dispatch(
      updateConsultation(consultationId, {
        status: ConsultationStatus.Accepted,
      })
    )
  }

  const handleRefuseConsultation = (consultationId: string) => {
    dispatch(
      updateConsultation(consultationId, {
        status: ConsultationStatus.Refused,
      })
    )
  }

  return !consultations.length ? (
    <Stack direction="column" justifyContent="center" alignItems="center">
      <CardMedia component={NoResults} sx={{ width: 'auto', height: '250px', mt: '10px', mr: '40px' }} />
      <Typography component="div" variant="body1">
        {t('page.consultations.noConsultationMessage')}
      </Typography>
    </Stack>
  ) : (
    <ConsultationsTable
      data={consultationsData}
      isDoctor={isStaff(authUser) && authUser.status === 'MEDECIN'}
      onAcceptConsultation={handleAcceptConsultation}
      onRefuseConsultation={handleRefuseConsultation}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={limitOptions}
      totalCount={_.get(consultationSearchResult, 'totalCount', 0)}
    />
  )
}

export default ConsultationsTableContainer
